import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { PHOTOS } from '../mock';
import { adminApi } from '../api';
import { resizeImageFile, isImageFile } from '../lib/imageResize';
import { Image as ImageIcon, Plus, X, Loader2, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE_KEYS = ['wedding', 'portrait', 'corporate', 'birthday'];

const CreateSubgalleryDialog = ({ open, onOpenChange, galleryId, onCreated }) => {
  const [title, setTitle] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const [dragOver, setDragOver] = useState(false);
  const [reading, setReading] = useState(false);

  const readFiles = async (files) => {
    const list = Array.from(files || []);
    const images = list.filter(isImageFile);
    const skipped = list.length - images.length;
    if (skipped > 0) toast.error(`${skipped} file ignorati (non sono immagini)`);
    if (images.length === 0) return;
    setReading(true);
    for (const file of images) {
      try {
        const { dataUrl, filename } = await resizeImageFile(file);
        const uid = `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        setUploaded(prev => [...prev, { uid, data_url: dataUrl, filename }]);
      } catch (err) {
        toast.error(`Impossibile leggere ${file.name}`);
      }
    }
    setReading(false);
  };
  const addSample = (key) => {
    const list = PHOTOS[key].map((u,i)=>({ uid: `s-${Date.now()}-${i}`, data_url: u, filename: `${key}_${i+1}.jpg` }));
    setUploaded(list); toast.success(`${list.length} foto d'esempio aggiunte`);
  };
  const removePhoto = (i) => setUploaded(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Titolo obbligatorio');
    if (uploaded.length === 0) return toast.error('Aggiungi almeno una foto');
    setBusy(true); setProgress(0);
    try {
      const sub = await adminApi.createSubgallery(galleryId, { title: title.trim() });
      let done = 0;
      for (const p of uploaded) {
        await adminApi.addPhoto(sub.id, { filename: p.filename, data_url: p.data_url });
        done += 1; setProgress(Math.round((done / uploaded.length) * 100));
      }
      toast.success('Sotto-galleria creata');
      onCreated && await onCreated();
      onOpenChange(false);
      setTitle(''); setUploaded([]); setProgress(0);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v && !busy) { setTitle(''); setUploaded([]); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><FolderPlus className="w-5 h-5 text-[#B8944B]" /> Nuova sotto-galleria</DialogTitle>
          <DialogDescription>Aggiungi una cartella di foto (es. Preparativi, Cerimonia, Ricevimento).</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Titolo</span>
            <input required value={title} onChange={e=>setTitle(e.target.value)} placeholder="Es. Preparativi, Cerimonia, Ricevimento" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#F7F1E1]" />
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Foto ({uploaded.length})</span>
              <div className="flex items-center gap-2">
                {SAMPLE_KEYS.map(k => (
                  <button key={k} type="button" onClick={()=>addSample(k)} className="text-[10px] uppercase tracking-widest text-[#B8944B] hover:underline">demo {k}</button>
                ))}
              </div>
            </div>
            <label
              onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={(e)=>{ e.preventDefault(); setDragOver(false); readFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-8 cursor-pointer transition ${dragOver ? 'border-[#C9A961] bg-[#FAF5E6]/70' : 'border-slate-200 hover:border-[#C9A961] hover:bg-[#FAF5E6]/40'}`}>
              <ImageIcon className={`w-8 h-8 mb-2 ${reading ? 'text-[#C9A961] animate-pulse' : 'text-slate-400'}`} />
              <span className="text-sm text-slate-600">{reading ? 'Preparo le foto...' : 'Clicca o trascina qui le foto'}</span>
              <span className="text-xs text-slate-400 mt-1">JPG, PNG, HEIC — foto grandi ridimensionate automaticamente in alta qualità</span>
              <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={e=>{ readFiles(e.target.files); e.target.value=''; }} />
            </label>
            {uploaded.length > 0 && (
              <div className="grid grid-cols-6 gap-2 mt-3">
                {uploaded.slice(0, 18).map((u, i) => (
                  <div key={u.uid || `${u.filename}-${i}`} className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative group">
                    <img src={u.data_url} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={()=>removePhoto(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"><X className="w-3 h-3" /></button>
                    {i === 0 && <div className="absolute bottom-1 left-1 text-[9px] bg-[#C9A961] text-black font-bold px-1.5 py-0.5 rounded">COVER</div>}
                  </div>
                ))}
                {uploaded.length > 18 && <div className="aspect-square rounded-lg bg-slate-100 grid place-items-center text-xs text-slate-500">+{uploaded.length - 18}</div>}
              </div>
            )}
          </div>

          {busy && (
            <div className="bg-[#F7F1E1] border border-[#C9A961]/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm text-[#8A7434] mb-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento foto... {progress}%</div>
              <div className="h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-[#C9A961] transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" disabled={busy} onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50">Annulla</button>
            <button disabled={busy} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-70">
              <Plus className="w-4 h-4" /> {busy ? 'Creazione...' : 'Crea sotto-galleria'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSubgalleryDialog;
